import React, { useState, useEffect } from 'react';
import {
  createRgbImage,
  createRgbByCoordinates,
  createRgbByObject
} from '../../api/images';
import { CodePopup } from '../CodeSnippet';
import { FormInput, FormSelect, FormFileSelect, FormGroup } from './FormComponents';

// Reusable Dashboard card for consistent styling
const DashboardCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
        {title}
      </h2>
      {children}
    </div>
  );
};

export const RgbTool = ({ availableFiles, collectionId, prefilledData }) => {
  // Modes: 'coordinates' | 'object' | 'files'
  const [searchMode, setSearchMode] = useState('coordinates');

  // Files mode: selected files for red, green, blue channels
  const [rSelectedFile, setRSelectedFile] = useState(null);
  const [gSelectedFile, setGSelectedFile] = useState(null);
  const [bSelectedFile, setBSelectedFile] = useState(null);

  // Coordinates and object mode states
  const [ra, setRa] = useState('');
  const [dec, setDec] = useState('');
  const [coordinates, setCoordinates] = useState(''); // Combined RA, Dec input
  const [objectName, setObjectName] = useState('');

  // Filter states (for coordinates and object modes)
  const [rFilter, setRFilter] = useState('');
  const [gFilter, setGFilter] = useState('');
  const [bFilter, setBFilter] = useState('');

  // Handle prefilled data from ImageBrowser
  useEffect(() => {
    if (prefilledData) {
      if (prefilledData.ra_center !== undefined && prefilledData.dec_center !== undefined) {
        const raVal = prefilledData.ra_center.toString();
        const decVal = prefilledData.dec_center.toString();
        setRa(raVal);
        setDec(decVal);
        setCoordinates(`${raVal}, ${decVal}`);
      }
      if (prefilledData.object_name) {
        setObjectName(prefilledData.object_name);
      }
      // Set search mode to coordinates when prefilled
      setSearchMode('coordinates');
    }
  }, [prefilledData]);

  // Common controls
  const [size, setSize] = useState('5.0');
  const [sizeUnit, setSizeUnit] = useState('arcmin');
  const [stretch, setStretch] = useState('3.0');
  const [Q, setQ] = useState('8.0');
  const [pattern, setPattern] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rgbImageUrl, setRgbImageUrl] = useState(null);
  const [rgbImageData, setRgbImageData] = useState(null);
  
  // File search states
  const [rFileSearchText, setRFileSearchText] = useState('');
  const [gFileSearchText, setGFileSearchText] = useState('');
  const [bFileSearchText, setBFileSearchText] = useState('');
  const [rFilteredFiles, setRFilteredFiles] = useState([]);
  const [gFilteredFiles, setGFilteredFiles] = useState([]);
  const [bFilteredFiles, setBFilteredFiles] = useState([]);
  const [codePopup, setCodePopup] = useState({ isOpen: false, snippetName: '', dynamicData: {} });

  // Unique and sorted list of file filters
  const filterOptions = [...new Set(
    availableFiles.filter(file => file.filter).map(file => file.filter)
  )].sort();

  // Parse coordinates from combined input
  const parseCoordinates = (coordStr) => {
    if (!coordStr) return { ra: '', dec: '' };
    // Support formats: "150.5, -23.4" or "150.5 -23.4"
    const parts = coordStr.split(/[,\s]+/).filter(p => p.trim());
    if (parts.length >= 2) {
      return { ra: parts[0].trim(), dec: parts[1].trim() };
    }
    return { ra: '', dec: '' };
  };

  // Handle combined coordinate input
  const handleCoordinatesChange = (value) => {
    setCoordinates(value);
    const { ra: parsedRa, dec: parsedDec } = parseCoordinates(value);
    setRa(parsedRa);
    setDec(parsedDec);
  };

  // Clean up URL object when component unmounts or when url changes
  useEffect(() => {
    return () => {
      if (rgbImageUrl) {
        URL.revokeObjectURL(rgbImageUrl);
      }
    };
  }, [rgbImageUrl]);
  
  // Update filtered files for each channel
  useEffect(() => {
    if (!availableFiles) {
      setRFilteredFiles([]);
      setGFilteredFiles([]);
      setBFilteredFiles([]);
      return;
    }
    
    // Filter files for red channel
    if (rFileSearchText) {
      const lowerSearch = rFileSearchText.toLowerCase();
      setRFilteredFiles(availableFiles.filter(file => {
        return file.filename?.toLowerCase().includes(lowerSearch) || 
               file.filter?.toLowerCase().includes(lowerSearch) || 
               file.object_name?.toLowerCase().includes(lowerSearch);
      }));
    } else {
      setRFilteredFiles(availableFiles);
    }
    
    // Filter files for green channel
    if (gFileSearchText) {
      const lowerSearch = gFileSearchText.toLowerCase();
      setGFilteredFiles(availableFiles.filter(file => {
        return file.filename?.toLowerCase().includes(lowerSearch) || 
               file.filter?.toLowerCase().includes(lowerSearch) || 
               file.object_name?.toLowerCase().includes(lowerSearch);
      }));
    } else {
      setGFilteredFiles(availableFiles);
    }
    
    // Filter files for blue channel
    if (bFileSearchText) {
      const lowerSearch = bFileSearchText.toLowerCase();
      setBFilteredFiles(availableFiles.filter(file => {
        return file.filename?.toLowerCase().includes(lowerSearch) || 
               file.filter?.toLowerCase().includes(lowerSearch) || 
               file.object_name?.toLowerCase().includes(lowerSearch);
      }));
    } else {
      setBFilteredFiles(availableFiles);
    }
  }, [availableFiles, rFileSearchText, gFileSearchText, bFileSearchText]);

  const handleCreateRgb = async () => {
    setError('');
    setLoading(true);

    try {
      let result;
      const commonOptions = {
        stretch: parseFloat(stretch),
        Q: parseFloat(Q),
        sizeUnit: sizeUnit
      };

      if (searchMode === 'files') {
        if (!rSelectedFile || !gSelectedFile || !bSelectedFile) {
          throw new Error('Please select files for all three channels.');
        }

        // Optional region options if provided
        if (ra && dec) {
          commonOptions.ra = parseFloat(ra);
          commonOptions.dec = parseFloat(dec);
          if (size) {
            commonOptions.size = parseFloat(size);
          }
        }

        result = await createRgbImage(
          rSelectedFile.id,
          gSelectedFile.id,
          bSelectedFile.id,
          commonOptions
        );
      } else if (searchMode === 'coordinates') {
        if (!ra || !dec || !size || !rFilter || !gFilter || !bFilter) {
          throw new Error('Please provide coordinates, size, and filters.');
        }
        result = await createRgbByCoordinates(
          collectionId,
          parseFloat(ra),
          parseFloat(dec),
          parseFloat(size),
          rFilter,
          gFilter,
          bFilter,
          {
            ...commonOptions,
            pattern: pattern || undefined
          }
        );
      } else {
        // Object search mode
        if (!objectName || !ra || !dec || !rFilter || !gFilter || !bFilter) {
          throw new Error('Please provide an object name, coordinates, and filters.');
        }
        const objectOptions = {
          ...commonOptions,
          size: size ? parseFloat(size) : undefined
        };
        result = await createRgbByObject(
          collectionId,
          objectName,
          rFilter,
          gFilter,
          bFilter,
          {
            ...objectOptions,
            pattern: pattern || undefined
          }
        );
      }

      const url = URL.createObjectURL(result.data);
      setRgbImageUrl(url);
      setRgbImageData(result);
    } catch (err) {
      console.error('Error creating RGB image:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create RGB image.');
    } finally {
      setLoading(false);
    }
  };

  // Utility to render a simple spinner using an SVG
  const renderSpinner = () => (
    <div className="flex justify-center py-4">
      <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>
  );

  return (
    <DashboardCard title="Create RGB Color Image">
      {/* Tab Navigation */}
      <div className="flex border-b border-ui-light-border dark:border-ui-dark-border mb-4">
        <button
          onClick={() => setSearchMode('coordinates')}
          className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${searchMode === 'coordinates'
            ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
            : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'}`}
        >
          By Coordinates
        </button>
        <button
          onClick={() => setSearchMode('object')}
          className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${searchMode === 'object'
            ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
            : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'}`}
        >
          By Object Name
        </button>
        <button
          onClick={() => setSearchMode('files')}
          className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${searchMode === 'files'
            ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
            : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'}`}
        >
          By Files
        </button>
      </div>

      {/* Render form fields based on search mode */}
      {searchMode === 'coordinates' && (
        <>
          {/* Combined RA & Dec input */}
          <FormInput
            label="Coordinates (RA, Dec)"
            value={coordinates}
            onChange={(e) => handleCoordinatesChange(e.target.value)}
            placeholder="e.g., 150.5, -23.4 or 150.5 -23.4"
            helpText="Enter coordinates as: RA (0-360°), Dec (-90 to 90°)"
            required
            className="mb-4"
          />
          {/* Filter selection for each channel */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            {['Red', 'Green', 'Blue'].map((color, idx) => {
              const value = idx === 0 ? rFilter : idx === 1 ? gFilter : bFilter;
              const onChange =
                idx === 0
                  ? (e) => setRFilter(e.target.value)
                  : idx === 1
                  ? (e) => setGFilter(e.target.value)
                  : (e) => setBFilter(e.target.value);
              return (
                <FormSelect
                  key={color}
                  label={`${color} Filter`}
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: 'Select a filter' },
                    ...filterOptions.map(filter => ({ value: filter, label: filter }))
                  ]}
                  required
                />
              );
            })}
          </div>
        </>
      )}

      {searchMode === 'object' && (
        <>
          <FormInput
            label="Object Name"
            value={objectName}
            onChange={(e) => setObjectName(e.target.value)}
            placeholder="Enter object name"
            required
            className="mb-4"
          />
          {/* Combined RA & Dec input */}
          <FormInput
            label="Coordinates (RA, Dec)"
            value={coordinates}
            onChange={(e) => handleCoordinatesChange(e.target.value)}
            placeholder="e.g., 150.5, -23.4 or 150.5 -23.4"
            helpText="Enter coordinates as: RA (0-360°), Dec (-90 to 90°)"
            required
            className="mb-4"
          />
          {/* Filter selectors */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            {['Red', 'Green', 'Blue'].map((color, idx) => {
              const value = idx === 0 ? rFilter : idx === 1 ? gFilter : bFilter;
              const onChange =
                idx === 0
                  ? (e) => setRFilter(e.target.value)
                  : idx === 1
                  ? (e) => setGFilter(e.target.value)
                  : (e) => setBFilter(e.target.value);
              return (
                <FormSelect
                  key={color}
                  label={`${color} Filter`}
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: 'Select a filter' },
                    ...filterOptions.map(filter => ({ value: filter, label: filter }))
                  ]}
                  required
                />
              );
            })}
          </div>
        </>
      )}

      {searchMode === 'files' && (
        <>
          {/* File selection for each channel */}
          <div className="grid grid-cols-1 gap-4 mb-4">
            {[
              { label: 'Red Channel File', value: rSelectedFile, setter: setRSelectedFile, searchText: rFileSearchText, setSearchText: setRFileSearchText, filteredFiles: rFilteredFiles },
              { label: 'Green Channel File', value: gSelectedFile, setter: setGSelectedFile, searchText: gFileSearchText, setSearchText: setGFileSearchText, filteredFiles: gFilteredFiles },
              { label: 'Blue Channel File', value: bSelectedFile, setter: setBSelectedFile, searchText: bFileSearchText, setSearchText: setBFileSearchText, filteredFiles: bFilteredFiles }
            ].map((channel) => (
              <FormFileSelect
                key={channel.label}
                label={channel.label}
                searchValue={channel.searchText}
                onSearchChange={(e) => channel.setSearchText(e.target.value)}
                selectedValue={channel.value ? channel.value.id : ''}
                onSelectChange={(e) => {
                  const fileId = e.target.value;
                  // Use == for type coercion between string and number
                  const file = channel.filteredFiles.find(f => f.id == fileId);
                  channel.setter(file);
                }}
                files={channel.filteredFiles}
                required
              />
            ))}
          </div>
          {/* Optional region options using a details/summary (accordion-like) */}
          <details className="mb-4 border border-ui-light-border dark:border-ui-dark-border rounded-xl">
            <summary className="cursor-pointer p-2 text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
              Region Options (Optional)
            </summary>
            <div className="p-2">
              <FormInput
                label="Coordinates (RA, Dec)"
                value={coordinates}
                onChange={(e) => handleCoordinatesChange(e.target.value)}
                placeholder="e.g., 150.5, -23.4 or 150.5 -23.4"
                helpText="Optional: Enter coordinates as: RA (0-360°), Dec (-90 to 90°)"
              />
            </div>
          </details>
        </>
      )}

      {/* Common size and unit controls */}
      <FormGroup className="mb-4 mt-2">
        <FormInput
          label="Size"
          type="number"
          value={size}
          onChange={(e) => setSize(e.target.value)}
          placeholder="Enter size"
          step="0.1"
          min="0.1"
          required
          className="flex-1"
        />
        <FormSelect
          label="Unit"
          value={sizeUnit}
          onChange={(e) => setSizeUnit(e.target.value)}
          options={[
            { value: 'arcmin', label: 'arcmin' },
            { value: 'arcsec', label: 'arcsec' },
            { value: 'pixels', label: 'pixel' }
          ]}
          className="w-32"
        />
      </FormGroup>
      
      {/* Pattern field */}
      <FormInput
        label="Pattern (optional)"
        value={pattern}
        onChange={(e) => setPattern(e.target.value)}
        placeholder="Optional filename pattern to match"
        helpText="Filter files by partial filename match"
        className="mb-4"
      />

      {/* Accordion for RGB Composition Options */}
      <details className="mb-4 border border-ui-light-border dark:border-ui-dark-border rounded-xl">
        <summary className="cursor-pointer p-2 text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
          RGB Composition Options
        </summary>
        <div className="p-2 grid grid-cols-2 gap-4">
          <FormInput
            label="Stretch"
            type="number"
            value={stretch}
            onChange={(e) => setStretch(e.target.value)}
            step="0.1"
            min="0.1"
            disabled={loading}
            helpText="Controls the stretch of faint features"
          />
          <FormInput
            label="Q (saturation)"
            type="number"
            value={Q}
            onChange={(e) => setQ(e.target.value)}
            step="0.5"
            min="0.1"
            disabled={loading}
            helpText="Controls color saturation"
          />
        </div>
      </details>

      {/* Error Message */}
      {error && (
        <div className="p-2 mt-4 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
          {error}
        </div>
      )}

      {/* Submit Buttons */}
      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={handleCreateRgb}
          disabled={loading}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        >
          {loading ? renderSpinner() : 'Create RGB Image'}
        </button>
        <button
          type="button"
          onClick={() => {
            const snippetName = searchMode === 'coordinates' ? 'rgb_by_coordinates' :
                              searchMode === 'object' ? 'rgb_by_object' : 'rgb_by_files';
            setCodePopup({
              isOpen: true,
              snippetName,
              dynamicData: {
                base_url: window.location.origin,
                collection_id: collectionId,
                ra: ra || '0.0',
                dec: dec || '0.0',
                size: size || '5.0',
                size_unit: sizeUnit,
                r_filter: rFilter || 'R',
                g_filter: gFilter || 'G',
                b_filter: bFilter || 'B',
                stretch: stretch,
                Q: Q,
                pattern: pattern || '',
                object_name: objectName || '',
                r_file_id: rSelectedFile?.id || '',
                g_file_id: gSelectedFile?.id || '',
                b_file_id: bSelectedFile?.id || ''
              }
            });
          }}
          className="px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-hover dark:bg-ui-dark-hover rounded-md hover:bg-ui-light-card dark:hover:bg-ui-dark-card focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors flex items-center gap-2"
          title="Show Python code"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Code
        </button>
      </div>

      {/* Display RGB Image */}
      {rgbImageUrl && (
        <div className="mt-4 flex flex-col items-center gap-4">
          <div className="p-2 bg-ui-light-card dark:bg-ui-dark-card rounded-md shadow-md">
            <img
              src={rgbImageUrl}
              alt="RGB composite"
              className="max-w-full max-h-[400px]"
            />
          </div>
        </div>
      )}

      {/* Image Metadata */}
      {rgbImageData?.metadata && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
            RGB Image Metadata:
          </h3>
          <pre className="max-h-40 overflow-auto bg-ui-light-card dark:bg-ui-dark-card p-4 rounded-md text-xs text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
            {JSON.stringify(rgbImageData.metadata, null, 2)}
          </pre>
        </div>
      )}

      {/* Code Popup */}
      <CodePopup
        isOpen={codePopup.isOpen}
        onClose={() => setCodePopup({ isOpen: false, snippetName: '', dynamicData: {} })}
        snippetName={codePopup.snippetName}
        dynamicData={codePopup.dynamicData}
      />
    </DashboardCard>
  );
};

export default RgbTool;